import React, { useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { useNavigate } from "react-router-dom"
import { FaCheck } from "react-icons/fa6"
import { IoBanOutline } from "react-icons/io5"
import { useQueryClient } from "@tanstack/react-query"
import useLogging from "../hooks/useLogging"

const TermsConditions = ({ sidebarOpen }) => {
	const { user, getAccessTokenSilently, logout } = useAuth0()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [loading, setLoading] = useState(false)
	const logging = useLogging()

	const handleAccept = async () => {
		setLoading(true)
		try {
			const token = await getAccessTokenSilently()

			// Make a POST request to the backend route to update the user's app_metadata
			const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user`, {
				// Replace with your actual endpoint
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}` // Include the user's auth token if needed
				},
				body: JSON.stringify({
					user_metadata: { termsAccepted: true }
				})
			})

			if (!response.ok) {
				throw new Error("Failed to update user metadata")
			}
			logging.info("Terms acceptance updated successfully")
			await queryClient.invalidateQueries({ queryKey: ["user"] })
			navigate("/home") // Redirect to the main app after acceptance
		} catch (error) {
			logging.error("Error updating terms acceptance:", error)
		} finally {
			setLoading(false)
		}
	}

	const handleLogoutAuth0 = event => {
		event.preventDefault()
		event.stopPropagation()
		const returnToUrl = `${window.location.origin}/`
		logging.info("Logging out")
		logout({
			returnTo: returnToUrl
		})
	}

	const orgnization = {
		org_Joxjbsjx16wHTdej: {
			org_name: "Children's Hospital Los Angeles",
			org_short_name: "CHLA"
		},
		org_6f0XbU6TY7YG4KgW: { org_name: "Main Line Health", org_short_name: "MLH" },
		org_lkg3WMhi3Q2l92iT: {
			org_name: "Oncare AI (Test Environment)",
			org_short_name: "Oncare AI"
		}
	}

	return (
		<div className={`${sidebarOpen ? "pl-64" : "pl-8"}`}>
			<div className="p-8">
				<div className="text-center mb-6">
					<h1 className="text-3xl font-bold font-manrope text-gray-800">
						Terms and Conditions
					</h1>
					<p className="text-gray-600 mt-2">
						Please review our terms carefully before proceeding.
					</p>
				</div>

				<div className="bg-white shadow-inner rounded-lg overflow-hidden border border-gray-200">
					<div className="overflow-y-auto max-h-[calc(90vh-200px)] p-6">
						<h2 className="text-xl font-semibold text-gray-700 mb-4">Overview</h2>
						<p className="text-gray-700 leading-7 mb-6">
							1. Introduction
							<br />
							Welcome to the Oncare Platform (the "Platform"). These Terms and
							Conditions (the "Terms") govern your use of the Platform provided by
							Oncore Health, Inc. ("Oncore", also known as "Oncare"). By accessing or
							using the Platform, you agree to comply with these Terms. If you do not
							agree, you must not use the Platform.
						</p>
						<p className="text-gray-700 leading-7 mb-6">
							2. Scope of Use
							<br />
							Oncore has developed a clinical operations platform to improve patient
							access and reduce administrative burden for infusion center care teams.
							This application helps optimize infusion center operations, including
							capacity management, scheduling, and staffing, to improve efficiency and
							nursing experience.
							<br />
							Access to the Platform is restricted to authorized users affiliated with{" "}
							{orgnization[user.org_id].org_name} ("
							{orgnization[user.org_id].org_short_name}") and is provided under the
							terms of {orgnization[user.org_id].org_short_name}'s agreement with
							Oncore.
						</p>
						<p className="text-gray-700 leading-7 mb-6">
							3. Compliance with the Master Services Agreement (MSA)
							<br />
							Certain provisions, including but not limited to limitations of
							liability, warranties and disclaimers, termination, and data protection
							and security, are governed by the Master Services Agreement ("MSA")
							between {orgnization[user.org_id].org_short_name} and Oncore. For more
							details about specific terms, rules, and restrictions, please refer to
							the MSA or contact your {orgnization[user.org_id].org_short_name}{" "}
							administrator.
						</p>
						<p className="text-gray-700 leading-7 mb-6">
							4. Intellectual Property
							<br />
							The intellectual property rights related to the Platform are governed by
							the MSA between {orgnization[user.org_id].org_short_name} and Oncore.
							Oncore retains all ownership and rights to the Platform, including but
							not limited to its software, algorithms, and improvements. For more
							details, please refer to the MSA.
						</p>
						<p className="text-gray-700 leading-7 mb-6">
							5. User Responsibilities
							<br />
							As an authorized user, you agree to:
							<br />• Use the Platform only for purposes authorized by{" "}
							{orgnization[user.org_id].org_short_name} and consistent with these
							Terms.
							<br />
							• Protect the confidentiality of your login credentials and immediately
							report any unauthorized access or suspicious activity.
							<br />• Comply with {orgnization[user.org_id].org_short_name}'s policies
							and procedures related to patient data and use of the Platform.
						</p>
						<p className="text-gray-700 leading-7 mb-6">
							6. Prohibited Actions
							<br />
							You must not:
							<br />
							• Attempt to access data or features you are not authorized to use.
							<br />
							• Share your login credentials with others.
							<br />
							• Reverse-engineer, decompile, or otherwise attempt to derive the source
							code or underlying algorithms of the Platform.
							<br />• Use the Platform for personal, commercial, or competitive
							purposes unrelated to {orgnization[user.org_id].org_short_name}'s
							operations.
						</p>
						<p className="text-gray-700 leading-7 mb-6">
							7. Data Protection and Security
							<br />
							Oncore complies with data protection and security obligations under the
							MSA and applicable healthcare regulations, including HIPAA. Oncore
							reserves the right to generate and utilize anonymized, aggregated data
							for internal business purposes (e.g., improving the Platform's
							performance) during or after the term of the agreement. For more details
							about data use, please refer to the MSA.
							<br />
							While Oncore provides secure access to the Platform, individual users
							are responsible for:
							<br />
							• Safeguarding login credentials.
							<br />• Ensuring that any actions taken within the Platform comply with{" "}
							{orgnization[user.org_id].org_short_name}'s policies and procedures.
						</p>
						<p className="text-gray-700 leading-7 mb-6">
							8. Platform Updates and Availability
							<br />
							Oncore reserves the right to update or modify the Platform to improve
							functionality. Oncore will strive to minimize disruptions during updates
							but does not guarantee uninterrupted availability.
						</p>
						<p className="text-gray-700 leading-7 mb-6">
							9. Limitation of Liability and Warranties
							<br />
							The limitations of liability and warranties related to the Platform are
							outlined in the MSA between {
								orgnization[user.org_id].org_short_name
							}{" "}
							and Oncore. For individual users, the Platform is provided "as-is," and
							Oncore disclaims liability for any misuse or unauthorized access.
						</p>
						<p className="text-gray-700 leading-7 mb-6">
							10. Termination
							<br />
							Oncore reserves the right to terminate or suspend individual user
							accounts for violations of these Terms. The overall termination terms
							for the Platform's availability and service are governed by the MSA.
						</p>
						<p className="text-gray-700 leading-7 mb-6">
							11. Governing Law
							<br />
							These Terms shall be governed by and construed in accordance with the
							laws of the State of Delaware.
						</p>
						<p className="text-gray-700 leading-7 mb-6">
							12. Contact Information
							<br />
							If you have questions about these Terms or need support, please contact:
							<br />• Email:{" "}
							<a href="mailto:info@useoncare.com" className="text-blue-500">
								info@useoncare.com
							</a>
						</p>
						<p className="text-gray-700 leading-7 mb-6">
							By using the Platform, you acknowledge and agree to these Terms. For
							further clarification, please refer to the MSA or contact your{" "}
							{orgnization[user.org_id].org_short_name} administrator.
						</p>
					</div>
				</div>

				<div className="flex justify-end space-x-4 mt-6">
					<button
						onClick={handleLogoutAuth0}
						className="flex items-center justify-center px-5 py-3 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all duration-150"
					>
						<IoBanOutline className="mr-2" />
						Reject
					</button>
					<button
						onClick={handleAccept}
						className="flex items-center justify-center px-5 py-3 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-all duration-150 disabled:opacity-50"
						disabled={loading}
					>
						{loading ? (
							<>
								<span className="loader mr-2"></span>Processing...
							</>
						) : (
							<>
								<FaCheck className="mr-2" />
								Accept
							</>
						)}
					</button>
				</div>
			</div>
		</div>
	)
}

export default TermsConditions
